import React from 'react';
import PropTypes from 'prop-types';
import {
    Fields,
    SingleSelect,
    HorizontalFlex,
    FieldLabel,
    ButtonWithIconSecondary,
    icons,
    Input,
} from 'smartsuite-ui';
import { useLabelContext } from '../hooks/labelContext';
import {
    getStringPredicateOptions,
    buildFilterWarning,
} from './filtersModalUtils';
import validateInputAgainstFieldRegex from './regexValidatorUtil';

/* eslint-disable complexity */
export default function FilterString(props) {
    const { dashboardLabels, predicateLabels } = useLabelContext();

    const predicateOptions = getStringPredicateOptions(
        predicateLabels,
        props.availableFilter.predicateOptions
    );

    const value = props.filter?.values?.[0] || '';

    function handleFilterPredicateChange(predicate) {
        props.updateFilter({
            [props.filterKey]: {
                values: [],
                ...props.filter,
                predicate,
            },
        });
    }

    function handleFilterValueChange(event) {
        props.updateFilter({
            [props.filterKey]: {
                predicate: predicateOptions[0],
                ...props.filter,
                values: event.target.value ? [event.target.value] : [],
            },
        });
    }

    function isValid() {
        const isDefaultValid = !props.isRequired || !!value;

        if (!props.availableFilter.fieldRegex) {
            return isDefaultValid;
        }

        const passesRegex = validateInputAgainstFieldRegex(
            value,
            props.availableFilter.fieldRegex
        );

        return isDefaultValid && passesRegex;
    }

    function renderFilterLabel() {
        const label =
            props.showAsterisk && props.isRequired
                ? `${props.availableFilter.label}*`
                : props.availableFilter.label;

        if (!props.isAdditionalFilter) {
            return label;
        }

        return (
            <HorizontalFlex>
                <FieldLabel>{label}</FieldLabel>
                <ButtonWithIconSecondary
                    iconClass={icons.x}
                    onClick={props.removeAdditionalFilter(props.filterKey)}
                />
            </HorizontalFlex>
        );
    }

    return (
        <Fields
            cssClassModifiers={[
                'no-background',
                'no-pad-top-bottom',
                'no-wrap',
                props.isEditor ? 'column' : '',
            ]}
        >
            <SingleSelect
                disabled={props.disabled}
                isClearable={false}
                label={renderFilterLabel()}
                menuPortalTarget={props.isEditor ? document.body : null}
                onChange={handleFilterPredicateChange}
                options={predicateOptions}
                styles={{
                    menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 10,
                    }),
                }}
                textField="textField"
                valueField="valueField"
                value={props.filter?.predicate ?? predicateOptions[0]}
            />
            <Input
                disabled={props.disabled}
                isValid={isValid()}
                onChange={handleFilterValueChange}
                placeholder={props.availableFilter.placeholder}
                value={value}
            />
            {props.availableFilter.appliesToDataSubset && (
                <div className="filter-warning-icon">
                    <span
                        className={icons.warningFilled}
                        title={buildFilterWarning(
                            props.availableFilter.label,
                            dashboardLabels
                        )}
                    />
                </div>
            )}
        </Fields>
    );
}
/* eslint-enable complexity */

FilterString.propTypes = {
    availableFilter: PropTypes.instanceOf(Object).isRequired,
    disabled: PropTypes.bool.isRequired,
    filter: PropTypes.instanceOf(Object),
    filterKey: PropTypes.string.isRequired,
    isAdditionalFilter: PropTypes.bool.isRequired,
    isEditor: PropTypes.bool,
    isRequired: PropTypes.bool.isRequired,
    removeAdditionalFilter: PropTypes.func.isRequired,
    showAsterisk: PropTypes.bool.isRequired,
    updateFilter: PropTypes.func.isRequired,
};

FilterString.defaultProps = {
    filter: {},
    isEditor: false,
};
